/**
 * useBackHref — resolves the "back" target for pages that expose a
 * "Back to Properties" / "Back to favourites" / etc. hyperlink.
 *
 * Priority chain, most-explicit first:
 *   1. Router state (`state.from`) — a caller passed the origin explicitly
 *      via `<Link state={{ from }}>` or `navigate(..., { state: { from } })`.
 *   2. sessionStorage stash written by `<NavigationHistoryTracker />` — this
 *      is the *previous* pathname (excluding the current page itself and
 *      any well-known "back-target" pages so we never return to a page
 *      whose own Back link would just bounce back).
 *   3. `document.referrer` when the visitor came in via a full-page nav
 *      from the same origin.
 *   4. `defaultHref` fallback (e.g. `/properties`).
 *
 * `defaultLabel` and the returned `label` let callers keep a friendly
 * button copy without hard-coding a phrase — we auto-pick "Back to
 * properties", "Back to favourites", "Back to home" or "Back" based on
 * where the resolved href actually points.
 */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export const BACK_STASH_KEY = "slh.nav.prev.v1";

function labelFor(href, fallback) {
  if (!href) return fallback;
  if (href === "/") return "Back to home";
  if (href.startsWith("/properties")) return "Back to properties";
  if (href.startsWith("/my-favourites")) return "Back to favourites";
  if (href.startsWith("/my-alerts")) return "Back to alerts";
  if (href.startsWith("/property-admin/dashboard")) return "Back to dashboard";
  return fallback || "Back";
}

export function useBackHref(defaultHref = "/properties", defaultLabel) {
  const location = useLocation();

  const href = useMemo(() => {
    // (1) Explicit router state
    if (location.state?.from) return location.state.from;
    // (2) sessionStorage stash written by NavigationHistoryTracker
    try {
      const stash = window.sessionStorage.getItem(BACK_STASH_KEY);
      if (stash) return stash;
    } catch { /* ignore quota */ }
    // (3) same-origin document.referrer for full-page loads
    const ref = (typeof document !== "undefined" && document.referrer) || "";
    if (ref) {
      try {
        const url = new URL(ref);
        if (
          url.origin === window.location.origin &&
          url.pathname !== window.location.pathname
        ) {
          return `${url.pathname}${url.search}`;
        }
      } catch { /* noop */ }
    }
    // (4) Fallback
    return defaultHref;
  }, [location.state, defaultHref]);

  const label = useMemo(() => labelFor(href, defaultLabel), [href, defaultLabel]);
  return { href, label };
}
